"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Play, Sparkles, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HumanityImpactHero() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const avatars = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_1.png-wvc-sizes",
    fallback: "AL",
    alt: "Community member Alex"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_2.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_2.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_2.webp-wvc-sizes",
    fallback: "MR",
    alt: "Community member Maya"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_3.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_3.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_3.webp-wvc-sizes",
    fallback: "JN",
    alt: "Community member Jordan"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_4.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_4.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_member_4.webp-wvc-sizes",
    fallback: "SK",
    alt: "Community member Sara"
  }];
  return <section data-section-id="118" id="humanityimpacthero" className="bg-background text-foreground relative overflow-hidden min-h-screen flex items-center px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-lg blur-3xl opacity-40" style={{
      background: "radial-gradient(circle, var(--color-primary) 0%, transparent 70%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-40 h-[36rem] w-[36rem] rounded-lg blur-3xl opacity-40" style={{
      background: "radial-gradient(circle, var(--color-primary) 0%, transparent 70%)"
    }} />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: "radial-gradient(circle, var(--color-foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-10 items-center">
          {}
          <div ref={contentRef} className={`lg:col-span-3 flex flex-col gap-8 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="outline" className="font-default border-border bg-card/40 backdrop-blur-sm px-3 py-1.5 text-xs tracking-widest uppercase w-fit gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg opacity-75" style={{
                background: "var(--color-primary)"
              }} />
                <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
                background: "var(--color-primary)"
              }} />
              </span>
              Educational Hub
            </Badge>

            <h1 className="font-default font-semibold tracking-tight leading-[1.1]" style={{
            fontSize: "clamp(2.5rem, 6vw, 5rem)"
          }}>
              <span className="block text-foreground">Humanity Awareness</span>
              <span className="block bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-foreground) 100%)"
            }}>
                Library
              </span>
            </h1>

            <p className="text-muted-foreground text-lg sm:text-xl leading-relaxed" style={{
            maxWidth: "48ch"
          }}>
              Our educational library confronts the urgent crises facing our global family—from women’s equality and poverty to homelessness, domestic violence, and the protection of our veterans and children in crisis.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Button size="lg" asChild className="font-default h-12 px-7 text-base font-medium shadow-lg border-0 text-primary-foreground hover:opacity-90 transition-opacity" style={{
              background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-foreground) 100%)"
            }}>
                <Link to="/CurrentCausesPage">
                  <Sparkles className="size-4" />
                  Explore Causes
                </Link>
              </Button>
              <Button size="lg" variant="ghost" asChild className="font-default h-12 px-6 text-base font-medium text-foreground hover:bg-accent/40">
                <Link to="/ShopPage">
                  <Play className="size-4" />
                  Shop Current Drop
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2">
                {avatars.map((avatar, i) => <SafeHydrate><Avatar key={i} data-index={i} className="size-9 ring-2 ring-background">
                    <AvatarImage src={avatar.src} data-wvc-srcset={avatar.srcset} data-wvc-sizes={avatar.sizes} alt={avatar.alt} loading="lazy" decoding="async" />
                    <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
                      {avatar.fallback}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>)}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => <Star key={i} data-index={i} className="size-3.5 fill-current" style={{
                  color: "var(--color-primary)"
                }} />)}
                </div>
                <span className="text-muted-foreground">
                  <span className="text-foreground font-medium">5/5</span>{" "}
                  community impact rating
                </span>
              </div>
            </div>
          </div>

          {}
          <div ref={visualRef} className={`lg:col-span-2 relative transition-all duration-1000 delay-200 ease-out ${visualVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative" style={{
            perspective: "1500px"
          }}>
              <div className="relative" style={{
              animation: "tfwd-float 6s ease-in-out infinite"
            }}>
                <div className="relative" style={{
                transform: "rotateY(-8deg) rotateX(6deg)",
                transformStyle: "preserve-3d"
              }}>
                  <Card className="overflow-hidden border border-border bg-card shadow-4xl py-0">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/40">
                      <div className="flex gap-1.5">
                        <span className="size-2.5 rounded-lg bg-destructive/70" />
                        <span className="size-2.5 rounded-lg" style={{
                        background: "hsl(33 78% 53% / 0.7)"
                      }} />
                        <span className="size-2.5 rounded-lg" style={{
                        background: "var(--color-primary)"
                      }} />
                      </div>
                      <div className="flex-1 mx-3">
                        <div className="h-5 rounded-md bg-background/60 border border-border flex items-center px-2.5">
                          <span className="text-[10px] text-muted-foreground font-mono truncate">
                            foreverfresh.org/library
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="aspect-[4/3] bg-background">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/library_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/library_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/library_hero.png-wvc-sizes" alt="Humanitarian awareness library resources" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tfwd-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
      `}</style>
    </section>;
}